import { Button, Dialog, DialogActions, DialogContent, DialogContentText, Typography } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { headerTypographyProps } from '../../../styles/sxStyling';
import { Employee } from '../../../types/Employee';
import EmployeeSearchAutocomplete from '../../common/EmployeeSearchAutocomplete/EmployeeSearchAutocomplete';
import _ from 'lodash';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { useGetAllPreferences } from '../../../services/react-query/preferences';
import { Preference, PREFERENCES_VALID_KEYS } from '../../../types/Preference';
import PreferencePicker from '../../preferences/picker/PreferencePicker';
import { EventEmployee } from '../../../types/Event';

interface IAddEmployeeModalProps {
  open: boolean;
  alreadyAddedEmployees: EventEmployee[];
  onClose: () => void;
  onAdd: (employees: Employee[], selectedRole?: Preference | null) => void;
}

export default function AddEmployeeModal({ open, alreadyAddedEmployees, onClose, onAdd }: IAddEmployeeModalProps) {
  const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);
  const [selectedRole, setSelectedRole] = useState<Preference | null>(null);

  const { data: roles } = useGetAllPreferences({
    params: { filters: { keys: [PREFERENCES_VALID_KEYS.EMPLOYEE_ROLE] } },
    enabled: true,
  });

  const { disabled } = useContext(DisableContext);
  useEffect(() => {
    if (!open) {
      setSelectedEmployees([]);
      setSelectedRole(null);
    }
  }, [open]);

  useEffect(() => {
    setSelectedEmployees([]);
  }, [selectedRole]);

  const handleAdd = () => {
    onAdd(selectedEmployees, selectedRole);
    onClose();
  };

  const handleValueChange = (value: Preference | null | undefined) => {
    setSelectedRole(value ?? null);
  };

  const excludedUuids = alreadyAddedEmployees.filter((ee) => ee.employee_role_uuid === selectedRole?.uuid).map((ee) => ee.employee.uuid);

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogContentText>
        <Typography {...headerTypographyProps} sx={{ ml: 2, mt: 2 }}>
          Select Employees To Add
        </Typography>
        <Typography sx={{ ml: 2, mt: 1 }}>Please select the employees you would like to associate with this event.</Typography>
      </DialogContentText>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <PreferencePicker label="Employee Role" options={roles ?? []} value={selectedRole} loading={!roles} onValueChange={handleValueChange} />

        <EmployeeSearchAutocomplete<true, Employee>
          isMultiple
          excludedEmployees={excludedUuids}
          value={selectedEmployees}
          onChange={setSelectedEmployees}
          isDisabled={disabled}
          error={!selectedRole ? { helperText: 'Select role first' } : undefined}
          filters={selectedRole ? { employee_role: selectedRole.uuid! } : undefined}
        />
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', mx: 1 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" disabled={_.isEmpty(selectedEmployees)} onClick={handleAdd}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
